import { useState, useCallback, useEffect, useRef } from 'react';

export const useAsyncDebounce = (value, delay = 1000) => {
  const [debouncedValue, setDebouncedValue] = useState();

  const debounce = func => {
    let timer;
    return (...args) => {
      const context = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(context, args);
      }, delay);
    };
  };

  const startTimer = useCallback(
    debounce(val => {
      setDebouncedValue(val);
    }),
    [],
  );

  useEffect(() => {
    startTimer(value);
  }, [value]);

  return debouncedValue;
};

export function useDebouncedEffect(callback, deps, delay) {
  useEffect(() => {
    let cleanupFn;

    const handler = setTimeout(() => {
      const maybeCleanup = callback();
      if (typeof maybeCleanup === 'function') {
        cleanupFn = maybeCleanup;
      }
    }, delay);

    return () => {
      clearTimeout(handler);
      if (cleanupFn) cleanupFn();
    };
  }, deps);
}

export function useDebouncedMemo<T>(
  factory: () => T,
  deps: React.DependencyList,
  delay: number,
  defaultValue: T | null = null,
): T | null {
  const [debouncedValue, setDebouncedValue] = useState<T | null>(defaultValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      const newValue = factory();
      setDebouncedValue(newValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, deps);

  return debouncedValue;
}

export const useThrottle = (callback, delay) => {
  const timeoutRef = useRef(null);

  const throttledFunction = (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // @ts-expect-error - TS2322: Type 'Timeout' is not assignable to type 'null'.
    timeoutRef.current = setTimeout(() => {
      callback(...args); // Call the original function with the provided arguments
    }, delay);
  };

  return throttledFunction;
};

export default useThrottle;
